import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const DetailPage = () => {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [isFavorite, setIsFavorite] = useState(false);
  const [loading, setLoading] = useState(true);
  const [userEmail, setUserEmail] = useState("");

  // Fetch user details and check favorite status
  const fetchUserAndCheckFavorite = useCallback(
    async (token) => {
      try {
        const userRes = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/users/me`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const userData = userRes.data;
        setUserEmail(userData.email);

        // Check if blog is in wishlist
        const wishlistRes = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/blog-wishlists`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            params: {
              "filters[email][$eq]": userData.email,
              "filters[slug][$eq]": slug,
            },
          }
        );

        setIsFavorite(
          wishlistRes.data.data && wishlistRes.data.data.length > 0
        );
      } catch (error) {
        console.error("Error fetching user and wishlist status:", error);
      } finally {
        setLoading(false);
      }
    },
    [slug]
  );

  // Fetch the blog data using the slug
  useEffect(() => {
    const fetchBlog = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/blogs?filters[slug][$eq]=${slug}`
        );
        const data = await response.json();
        setBlog(data.data && data.data.length > 0 ? { id: data.data[0].id, ...(data.data[0].attributes || data.data[0]) } : null);
      } catch (error) {
        console.error("Error fetching blog:", error);
      }
    };

    fetchBlog();

    // Check user's wishlist status
    const token = localStorage.getItem("jwt");
    if (token) {
      fetchUserAndCheckFavorite(token);
    } else {
      setLoading(false);
    }
  }, [slug, fetchUserAndCheckFavorite]);

  // Handle favorite toggle
  const handleFavoriteToggle = async () => {
    const token = localStorage.getItem("jwt");

    if (!token) {
      alert("Please log in to add to wishlist.");
      return;
    }

    setLoading(true);

    try {
      if (isFavorite) {
        // Find and delete the wishlist entry
        const findRes = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/blog-wishlists`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            params: {
              "filters[email][$eq]": userEmail,
              "filters[slug][$eq]": slug,
            },
          }
        );

        if (findRes.data.data && findRes.data.data.length > 0) {
          const wishlistId = findRes.data.data[0].id;

          await axios.delete(
            `${process.env.REACT_APP_API_URL}/api/blog-wishlists/${wishlistId}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          setIsFavorite(false);
          toast.success("Removed from wishlist!", {
            position: "top-right",
            autoClose: 3000,
          });
        }
      } else {
        // Add to wishlist
        const addData = {
          data: {
            email: userEmail,
            slug: slug,
            blog: blog.id,
          },
        };

        await axios.post(
          `${process.env.REACT_APP_API_URL}/api/blog-wishlists`,
          addData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        setIsFavorite(true);
        toast.success("Added to wishlist!", {
          position: "top-right",
          autoClose: 3000,
        });
      }
    } catch (error) {
      console.error("Error toggling wishlist:", error);
      alert("Failed to update wishlist. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!blog) {
    return <p>Loading...</p>;
  }

  return (
    <article className="max-w-7xl px-6 py-24 mx-auto space-y-12 dark:bg-gray-800 dark:text-gray-50 relative">
      {/* Favorite Toggle Button */}
      <button
        onClick={handleFavoriteToggle}
        className="absolute top-28 right-8 text-yellow-500 hover:text-yellow-400 transition duration-300"
        disabled={loading}
      >
        {isFavorite ? (
          <span className="text-3xl">★</span>
        ) : (
          <span className="text-3xl">☆</span>
        )}
      </button>

      {/* title and date */}
      <div className="w-full mx-auto space-y-4 text-center">
        <h1 className="text-4xl font-bold leading-tight md:text-5xl">
          {blog.title}
        </h1>
        <p className="text-sm dark:text-gray-400">
          by{" "}
          <span className="underline dark:text-violet-400">
            {blog.author}
          </span>{" "}
          on {new Date(blog.createdAt).toLocaleDateString()}
        </p>
      </div>

      {/* image */}
      <img
        src={blog.image}
        alt={blog.title}
        className="w-full h-auto max-h-96 object-cover dark:bg-gray-500"
      />

      {/* blog description */}
      <div className="dark:text-gray-100">
        {blog.longDescription}
      </div>

      {/* author */}
      <div className="pt-12 border-t dark:border-gray-700">
        <div className="flex flex-col space-y-4 md:space-y-0 md:space-x-6 md:flex-row">
          <img
            src={blog.avatar}
            alt={`Avatar of ${blog.author}`}
            className="self-center flex-shrink-0 w-24 h-24 border rounded-full object-cover dark:bg-gray-500 dark:border-gray-700"
          />
          <div className="flex flex-col">
            <h4 className="text-lg font-semibold">{blog.author}</h4>
            <p className="dark:text-gray-400">{blog.about}</p>
          </div>
        </div>
      </div>

      <ToastContainer />
    </article>
  );
};

export default DetailPage;
